import { useEffect, useRef, useState } from 'react';

import { DateField, Field, File, FileField, Text, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps } from 'src/lib/ComponentProps';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import ClockIconSvg from 'public/icons/clock.svg';

import styles from './FileDownload.module.css';

type FileDownloadProps = ComponentProps & {
	fields: {
		PublishedDate: Field<string>;
		Title: Field<string>;
		Subtitle: Field<string>;
		File: FileField;
		DownloadButtonText: Field<string>;
		DownloadCompleteText: Field<string>;
		PageText: Field<string>;
		PagesText: Field<string>;
		PublishedText: Field<string>;
		PageCount: Field<string>;
		ReadTime: Field<string>;
	};
};

export default withDatasourceCheck()<FileDownloadProps>(FileDownload);

function FileDownload(props: FileDownloadProps): JSX.Element {
	const isEditing = IsSitecoreEditing();

	const [downloadProgress, setDownloadProgress] = useState(0);
	const [showProgress, setShowProgress] = useState(false);
	const [isDownloadComplete, setIsDownloadComplete] = useState(false);

	const linkRef = useRef<HTMLDivElement | null>(null);

	const handleDownloadPDF = () => {
		setDownloadProgress(0);
		setShowProgress(true);
		setIsDownloadComplete(false);

		let progress = 0;

		const simulateProgress = (): Promise<void> => {
			return new Promise((resolve) => {
				// Simulate progress as the file is downloading
				const interval = setInterval(() => {
					progress += 10;
					setDownloadProgress(progress);

					// Stop once we hit 100% progress
					if (progress >= 100) {
						clearInterval(interval);
						resolve();
					}
				}, 100);
			});
		};

		// Simulate download progress bar
		simulateProgress()
			.then(() => {
				// Once progress reaches 100%, delay to make the transition smoother
				return new Promise((resolve) => setTimeout(resolve, 500));
			})
			.then(() => {
				// Finalize progress bar after delay
				setDownloadProgress(100); // Ensure it's 100% complete
				return new Promise((resolve) => setTimeout(resolve, 500));
			})
			.then(() => {
				// Hide progress bar and mark download complete
				setShowProgress(false);
				setIsDownloadComplete(true);
			})
			.catch((error) => {
				console.error('Failed to simulate download progress:', error);
				setShowProgress(false);
			});
	};

	useEffect(() => {
		const fileElement = linkRef.current;
		if (fileElement) {
			fileElement.addEventListener('click', handleDownloadPDF);
		}

		return () => {
			if (fileElement) {
				fileElement.removeEventListener('click', handleDownloadPDF);
			}
		};
	}, [linkRef]);

	if (!isEditing && !props.fields) {
		return <></>;
	}

	const fileSize = `${(parseInt(`${props.fields.File?.value?.size}` || '0') / (1024 * 1024)).toFixed(2)} MB`;

	return (
		<div className={styles.PDFDownloadContainer}>
			<div className={styles.PDFDownloadContent}>
				<div className={styles.PublishedDateContainer}>
					<div className={styles.PublishedDate}>
						<Text field={props.fields?.PublishedText} />
						<DateField field={props.fields?.PublishedDate} render={(date: Date) => date && date.toLocaleDateString('en-GB')} />
					</div>
					{showProgress && (
						<div className={styles.PDFProgressBar}>
							<span
								className={`${styles.PDFProgressBarPercentage} ${
									downloadProgress === 100 ? styles.PDFProgressBarGreen : styles.PDFProgressBarBlue
								}`}
								style={{ '--progress': `${Math.min(downloadProgress, 100)}%` } as React.CSSProperties}
							/>
						</div>
					)}
				</div>

				<div className={styles.PDFTitle}>
					<Text field={props.fields?.Title} />
				</div>

				{props.fields.Subtitle.value !== '' && (
					<div className={styles.PDFSubTitle}>
						<Text field={props.fields.Subtitle} />
					</div>
				)}

				{fileSize || props.fields.PageCount.value !== '' || props.fields.ReadTime.value !== '' ? (
					<div className={styles.PDFDetails}>
						{fileSize && <span>{fileSize}</span>}
						{props.fields.PageCount?.value !== '' && (
							<span>
								{props.fields.PageCount.value}{' '}
								{Number(props.fields.PageCount.value) <= 1 ? props.fields?.PageText?.value : props.fields?.PagesText?.value}
							</span>
						)}

						{props.fields.ReadTime?.value !== '' && (
							<span className={styles.readTimeContainer}>
								<ClockIconSvg className={styles.readTimeIcon} />
								<Text tag="span" field={props.fields.ReadTime} />
							</span>
						)}
					</div>
				) : null}
			</div>
			<div ref={linkRef}>
				<File
					field={props.fields.File}
					className={`button ${styles.PDFDownloadButton} ${isDownloadComplete ? styles.PDFDownloadButtonComplete : ''}`}
					disabled={downloadProgress > 0 && downloadProgress < 100}
				>
					{isDownloadComplete ? props.fields?.DownloadCompleteText?.value : props.fields?.DownloadButtonText?.value}
				</File>
			</div>
		</div>
	);
}
